import { executeCapability } from '../utils/capabilityExecute'
import { apiFetch } from '../utils/apiFetch'

export interface NotificationChannel {
  key: string
  push: boolean
  alert: boolean
  /** Workspace email. Omitted or false on rows saved before this channel existed. */
  email?: boolean
  /** Deliver to the subject user (person the event is about). */
  user?: boolean
  /** Deliver to others on this access level (fan-out). */
  fan?: boolean
  /** Sub-event for eventful types (independent row in Admin matrix). */
  event?: string
}

export interface PermissionLevel {
  _id: string;
  name: string;
  permissions: string[];
  /** Per-type email/push/alert channel flags. */
  notifications?: NotificationChannel[];
}

export type PermissionAppFlag = 'none' | 'external' | 'dev';

export interface PermissionKey {
  key: string;
  label: string;
  description?: string;
  /** True when this key only shows/hides an external dashboard app tile. */
  isExternalApp?: boolean;
  /** True when this key gates a developer/internal tool. Mutually exclusive with isExternalApp. */
  isDevApp?: boolean;
}

export function getPermissionAppFlag(perm: Pick<PermissionKey, 'isExternalApp' | 'isDevApp'>): PermissionAppFlag {
  if (perm.isDevApp) return 'dev';
  if (perm.isExternalApp) return 'external';
  return 'none';
}

class PermissionService {
  async listLevelNames(): Promise<string[]> {
    return executeCapability<string[]>({
      app: 'admin',
      capability: 'permissions.levelNames.list',
      input: {},
    })
  }

  async getAll(): Promise<PermissionLevel[]> {
    return executeCapability<PermissionLevel[]>({
      app: 'admin',
      capability: 'permissions.levels.list',
      input: {},
    })
  }

  async getAllPermissions(): Promise<PermissionKey[]> {
    return executeCapability<PermissionKey[]>({
      app: 'admin',
      capability: 'permissions.keys.list',
      input: {},
    })
  }

  async updatePermissionKey(
    key: string,
    data: { isExternalApp?: boolean; isDevApp?: boolean }
  ): Promise<PermissionKey> {
    return executeCapability<PermissionKey>({
      app: 'admin',
      capability: 'permissions.keys.update',
      input: { key, ...data },
    })
  }

  async setPermissionAppFlag(key: string, flag: PermissionAppFlag): Promise<PermissionKey> {
    if (flag === 'external') {
      return this.updatePermissionKey(key, { isExternalApp: true, isDevApp: false })
    }
    if (flag === 'dev') {
      return this.updatePermissionKey(key, { isExternalApp: false, isDevApp: true })
    }
    return this.updatePermissionKey(key, { isExternalApp: false, isDevApp: false })
  }

  async getMine(): Promise<string[]> {
    const token = localStorage.getItem('lts_jwt_token')
    const res = await apiFetch('/api/permissions/me', {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || payload?.success === false) {
      throw new Error(payload?.message || 'Failed to load permissions')
    }
    return Array.isArray(payload?.data) ? payload.data : []
  }

  async create(data: { name: string; permissions: string[]; notifications?: NotificationChannel[] }): Promise<PermissionLevel> {
    return executeCapability<PermissionLevel>({
      app: 'admin',
      capability: 'permissions.levels.create',
      input: data,
    })
  }

  async update(id: string, data: Partial<{ name: string; permissions: string[]; notifications: NotificationChannel[] }>): Promise<PermissionLevel> {
    return executeCapability<PermissionLevel>({
      app: 'admin',
      capability: 'permissions.levels.update',
      input: { id, ...data },
    })
  }

  async remove(id: string): Promise<void> {
    await executeCapability({
      app: 'admin',
      capability: 'permissions.levels.delete',
      input: { id },
    })
  }
}

export const permissionService = new PermissionService();
